import { Component } from '@theme/component';

/**
 * Bottom navigation dock for small viewports (Heights parity).
 *
 * Hidden by default; slides up once the shopper scrolls past the header and
 * slides away again near the top of the page. Buttons inside reuse the
 * theme's declarative events (`#search-modal/showDialog`, `#cart-drawer/toggle`);
 * the menu button forwards to the header drawer component below.
 *
 * @extends {Component}
 */
class MobileDock extends Component {
  /** @type {MediaQueryList} */
  #mobileMedia = window.matchMedia('(max-width: 749px)');

  /** @type {number} Scroll offset past which the dock reveals. */
  #threshold = 300;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#measureThreshold();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
    this.#mobileMedia.addEventListener('change', this.#onMediaChange);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.#mobileMedia.removeEventListener('change', this.#onMediaChange);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /**
   * Opens or closes the main menu drawer in the header.
   *
   * Clicks the drawer's <summary> instead of calling HeaderDrawer.toggle():
   * the component's open() only adds animation classes — the `open`
   * attribute is set by the summary click's native default action.
   */
  toggleMenu() {
    const summary = document.querySelector('header-drawer summary.header__icon--summary');
    if (summary instanceof HTMLElement) summary.click();
  }

  /** The dock reveals once the header has been scrolled past. */
  #measureThreshold() {
    const header = document.querySelector('#header-component');
    if (header instanceof HTMLElement) {
      this.#threshold = Math.max(120, header.offsetTop + header.offsetHeight);
    }
  }

  #onScroll = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #onResize = () => {
    this.#measureThreshold();
    this.#update();
  };

  #onMediaChange = () => this.#update();

  #update() {
    const active = this.#mobileMedia.matches && window.scrollY > this.#threshold;
    this.dataset.active = String(active);
  }
}

if (!customElements.get('mobile-dock')) {
  customElements.define('mobile-dock', MobileDock);
}
